import {useAccessToken, useIsOrgAdmin, useIsUnitUser, useUserClaimsData} from "../../services/auth"
import {useParams} from "react-router-dom"
import React from "react"
import {
	CheckActionButtons,
	CheckDepositInfo,
	disabledActionsEnsentaTooltipValue,
} from "../../components/CheckDeposits/CheckDepositInfo"
import {getButtonsToShow} from "../../components/CheckDeposits/checkDepositActions"
import {getPermittedButtons, hasAnyCheckDepositPermission} from "../../components/CheckDeposits/checkDepositPermissions"
import {useRefreshToken} from "../../hooks/useRefreshToken"
import {
	CheckDeposit,
	CheckDepositVendors,
	CheckImageSide,
	getCheckDepositImage,
	getCheckDepositRequest,
} from "../../resources/checkDeposits"
import {useAsyncResult} from "../../hooks/useAsyncResult"
import TitleBar, {MetaItemProps} from "../../components/TitleBar/TitleBar"
import {kebabCase, startCase} from "lodash"
import classNames from "classnames"
import MainSectionLayout from "../../containers/MainSection/MainSectionLayout"
import {OneProgramAdmonition} from "../../components/OneProgranAdmonition/OneProgramAdmonition"
import {Org} from "../../resources/org"
import {useQueryState} from "use-location-state"
import {useListReturnPath} from "../../hooks/useListReturnPath"
import {AsyncResultRequestState} from "../../types/asyncResult"
import {ErrorDocument, OkDocument} from "../../resources/common"
import {Account} from "../../resources/account"
import {Customer} from "../../resources/customer"
import {CheckDepositAssignments, CheckDepositAssignmentsProps} from "./CheckDepositAssignments"
import {AssigneeType, AssignmentStage} from "../../resources/assignments"
import {TabsMeta, TitleBarTabs} from "../../components/TitleBarTabs/TitleBarTabs"
import {IcomoonIconName} from "../../components/Icon/icons"
import {AssignmentsAmountIndicator} from "../../components/AssignmentAmountIndicator/AssignmentsAmountIndicator"
import {CheckImageCard} from "../../components/CheckDeposits/CheckImageCard"

export enum CheckDepositTabConstants {
	FullCheckDepositDetails = "FullCheckDepositDetails",
	ActionsRequired = "ActionsRequired",
}

export const checkDepositTabQueryStateName = "checkDepositTab" as const

interface CheckDepositMainSectionProps {
	frontImage: ArrayBuffer | undefined | null
	backImage: ArrayBuffer | undefined | null
	frontTiffImage?: ArrayBuffer | null
	backTiffImage?: ArrayBuffer | null
	expectsTiff?: boolean
	checkId: string
	cd: OkDocument<CheckDeposit>
	accessToken: string
	refresh: () => void
	getCheckDeposit: AsyncResultRequestState<OkDocument<CheckDeposit>, ErrorDocument>
}

function CheckDepositMainSection({
	frontImage,
	backImage,
	frontTiffImage,
	backTiffImage,
	expectsTiff,
	checkId,
	cd,
	accessToken,
	refresh,
	getCheckDeposit,
}: CheckDepositMainSectionProps) {
	return (
		<MainSectionLayout
			outerWrapperClassname={"check-deposit-full-with-details"}
			fullWidth={true}
			renderAfter={
				<div className="check-image-card-section">
					<CheckImageCard
						frontImage={frontImage}
						backImage={backImage}
						frontTiffImage={frontTiffImage}
						backTiffImage={backTiffImage}
						expectsTiff={expectsTiff}
						id={checkId}
						status={cd.data.attributes.status}
					/>
				</div>
			}
		>
			<CheckDepositInfo
				id={checkId}
				token={accessToken}
				refresh={refresh}
				useDrawer={false}
				checkDepositAsyncResult={getCheckDeposit}
			/>
		</MainSectionLayout>
	)
}

interface CheckDepositPageContentProps {
	isShowAssignments: boolean
	currentTab: CheckDepositTabConstants
	checkDepositAsMainSectionProps: CheckDepositMainSectionProps
	checkDepositAssignmentsProps: CheckDepositAssignmentsProps
}

interface CheckDepositPageContentVerifyVendorProps extends CheckDepositPageContentProps {
	checkId: string
	accessToken: string
}

function CheckDepositTabs({
	currentTab,
	setCurrentTab,
	checkDeposit,
	refreshToken,
}: {
	currentTab: CheckDepositTabConstants
	setCurrentTab: (tab: CheckDepositTabConstants) => void
	checkDeposit: CheckDeposit
	refreshToken: number
}) {
	const accessToken = useAccessToken()
	const claims = useUserClaimsData()
	const assigneeType = startCase(claims.userType) as AssigneeType
	const liClassName = "payment-tab-li" as const
	const commonTabProps = {liClassName}
	const tabs: TabsMeta<CheckDepositTabConstants>[] = [
		{
			label: startCase(CheckDepositTabConstants.FullCheckDepositDetails),
			key: CheckDepositTabConstants.FullCheckDepositDetails,
			prefixIcon: "payment-box-incoming" as IcomoonIconName,
		},
		{
			label: startCase(CheckDepositTabConstants.ActionsRequired),
			key: CheckDepositTabConstants.ActionsRequired,
			prefixIcon: "dialpad--business-products" as IcomoonIconName,
			suffixElement: (
				<AssignmentsAmountIndicator
					findAssignmentsArgs={{
						approvals: checkDeposit.relationships.approvals?.data.id,
						accessToken,
						assignees: [assigneeType],
						stages: [AssignmentStage.Pending],
						limit: 0,
					}}
					refreshToken={refreshToken}
					isActive={currentTab == CheckDepositTabConstants.ActionsRequired}
				/>
			),
		},
	].map((tab) => ({...tab, ...commonTabProps}))
	return <TitleBarTabs currentTab={currentTab} setCurrentTab={setCurrentTab} tabs={tabs} />
}

function CheckDepositPageContentVerifyVendor({
	isShowAssignments,
	currentTab,
	accessToken,
	checkId,
	checkDepositAsMainSectionProps,
	checkDepositAssignmentsProps,
}: CheckDepositPageContentVerifyVendorProps) {
	const frontTiffImageState = useAsyncResult(() =>
		getCheckDepositImage({
			accessToken,
			id: checkId,
			side: CheckImageSide.FrontTiff,
		})
	)
	const backTiffImageState = useAsyncResult(() =>
		getCheckDepositImage({
			accessToken,
			id: checkId,
			side: CheckImageSide.BackTiff,
		})
	)

	const frontTiffImage = frontTiffImageState.match(
		() => undefined,
		(image) => image,
		() => null
	)
	const backTiffImage = backTiffImageState.match(
		() => undefined,
		(image) => image,
		() => null
	)
	const tiffImageObj = {frontTiffImage, backTiffImage, expectsTiff: true}

	const props = {
		isShowAssignments,
		currentTab,
		checkDepositAsMainSectionProps: {
			...checkDepositAsMainSectionProps,
			...tiffImageObj,
		},
		checkDepositAssignmentsProps: {
			...checkDepositAssignmentsProps,
			...tiffImageObj,
		},
	}

	return <CheckDepositPageContent {...props} />
}

function CheckDepositPageContent({
	isShowAssignments,
	currentTab,
	checkDepositAsMainSectionProps,
	checkDepositAssignmentsProps,
}: CheckDepositPageContentProps) {
	if (!isShowAssignments) {
		return <CheckDepositMainSection {...checkDepositAsMainSectionProps} />
	}
	switch (currentTab) {
		case CheckDepositTabConstants.FullCheckDepositDetails:
			return <CheckDepositMainSection {...checkDepositAsMainSectionProps} />
		case CheckDepositTabConstants.ActionsRequired:
			return <CheckDepositAssignments {...checkDepositAssignmentsProps} />
	}
}

function hasApprovals(checkDeposit: CheckDeposit): boolean {
	return !!checkDeposit.relationships.approvals
}

export default function CheckDepositPage({
	tab = CheckDepositTabConstants.FullCheckDepositDetails,
}: {
	tab?: CheckDepositTabConstants
}) {
	const listReturnPath = useListReturnPath("/check-deposits/")
	const accessToken = useAccessToken()
	const isOrgAdmin = useIsOrgAdmin()
	const isUnitUser = useIsUnitUser()
	const {checkId = ""} = useParams<{checkId: string}>()
	const [refreshToken, refresh] = useRefreshToken()

	const [currentTab, setCurrentTab] = useQueryState<CheckDepositTabConstants>(checkDepositTabQueryStateName, tab)

	const getCheckDeposit = useAsyncResult(() => getCheckDepositRequest({accessToken, id: checkId}), [refreshToken])

	const frontImageState = useAsyncResult(() =>
		getCheckDepositImage({
			accessToken,
			id: checkId,
			side: CheckImageSide.Front,
		})
	)
	const backImageState = useAsyncResult(() =>
		getCheckDepositImage({
			accessToken,
			id: checkId,
			side: CheckImageSide.Back,
		})
	)

	const frontImage = frontImageState.match(
		() => undefined,
		(image) => image,
		() => null
	)

	const backImage = backImageState.match(
		() => undefined,
		(image) => image,
		() => null
	)

	return (
		<>
			{getCheckDeposit.match(
				() => null,
				(cd) => {
					const org = cd.included && (cd.included.find((i) => (i.type as string) === "org") as unknown as Org)
					const ContentComponent =
						cd.data.attributes.vendor === CheckDepositVendors.Veryfi
							? CheckDepositPageContentVerifyVendor
							: CheckDepositPageContent

					return (
						<>
							<TitleBar
								title={`#${checkId}`}
								breadcrumbs={[{text: "Check Deposits", path: listReturnPath}]}
								status={{
									text: startCase(cd.data.attributes.status),
									className: classNames("check-deposit-status", kebabCase(cd.data.attributes.status)),
								}}
								noAlignItems={hasApprovals(cd.data)}
								meta={[
									{
										text: "Account",
										icon: "bank-account--business-products",
										path: `/accounts/${cd.data.relationships.account?.data.id}`,
									},
									{
										text: "Customer",
										icon: "user-geometric-action-money-fund---users",
										path: `/customer/${cd.data.relationships.customer?.data.id}`,
									},
									...(cd.data.relationships.account?.data.id && cd.data.relationships.transaction?.data.id
										? [
												{
													text: "Transaction",
													icon: "money-transfer--business-products",
													path: `/transaction/${cd.data.relationships.account?.data.id}/${cd.data.relationships.transaction?.data.id}`,
												} as MetaItemProps,
										  ]
										: []),
								]}
							>
								{!hasApprovals(cd.data) &&
									hasAnyCheckDepositPermission(
										getButtonsToShow(cd.data.attributes.status, cd.data.attributes.vendor),
										isOrgAdmin
									) && (
										<CheckActionButtons
											id={checkId}
											refresh={refresh}
											buttons={getPermittedButtons(
												getButtonsToShow(cd.data.attributes.status, cd.data.attributes.vendor),
												isOrgAdmin
											)}
											title={
												cd.data.attributes.vendor === CheckDepositVendors.Ensenta && !isOrgAdmin && !isUnitUser
													? disabledActionsEnsentaTooltipValue
													: ""
											}
											disabled={cd.data.attributes.vendor === CheckDepositVendors.Ensenta && !isOrgAdmin && !isUnitUser}
											vendor={cd.data.attributes.vendor}
										/>
									)}
								{hasApprovals(cd.data) && (
									<div className="check-deposit-header-right">
										{hasAnyCheckDepositPermission(
											getButtonsToShow(cd.data.attributes.status, cd.data.attributes.vendor),
											isOrgAdmin
										) && (
											<div className="check-deposit-approval-action-buttons" style={{marginBottom: "8px"}}>
												<CheckActionButtons
													id={checkId}
													refresh={refresh}
													buttons={getPermittedButtons(
														getButtonsToShow(cd.data.attributes.status, cd.data.attributes.vendor),
														isOrgAdmin
													)}
													title={""}
													disabled={false}
													vendor={cd.data.attributes.vendor}
													small
												/>
											</div>
										)}
										<div className="check-deposit-header-tabs">
											<CheckDepositTabs
												currentTab={currentTab}
												setCurrentTab={setCurrentTab}
												checkDeposit={cd.data}
												refreshToken={refreshToken}
											/>
										</div>
									</div>
								)}
							</TitleBar>
							{org?.relationships?.parentOrg && <OneProgramAdmonition />}
							<ContentComponent
								isShowAssignments={hasApprovals(cd.data)}
								currentTab={currentTab}
								checkId={checkId}
								accessToken={accessToken}
								checkDepositAsMainSectionProps={
									{
										frontImage,
										backImage,
										checkId,
										cd,
										accessToken,
										refresh,
										getCheckDeposit,
									} as CheckDepositMainSectionProps
								}
								checkDepositAssignmentsProps={
									{
										checkDeposit: cd.data,
										included: cd.included as Array<Account | Customer>,
										frontImage: frontImage,
										backImage: backImage,
										refreshToken,
										refresh,
									} as CheckDepositAssignmentsProps
								}
							/>
						</>
					)
				},
				(_) => null
			)}
		</>
	)
}
